import { makeEquation } from './algebra.ts';
import type { LessonContent } from './types.ts';

export const LESSON_VERSION = 'grade7-linear-equations@1.0.0';

export const lesson: LessonContent = {
  id: 'grade7-linear-equations',
  version: LESSON_VERSION,
  grade: 7,
  title: 'What does an equation let you find?',
  objective:
    'Model and solve a one-variable, two-step equation, then interpret and verify its solution.',
  stages: {
    concept: {
      id: 'concept',
      title: 'The same value, two expressions',
      prompt:
        'Find the unknown. Choose an operation that preserves the solutions, and inspect what changes on both sides.',
      initialEquation: makeEquation(3, 6, 0, 18),
      expectedSolution: '4',
      unknownChoice: 'What does x represent?',
      unknownOptions: [
        { id: 'unknown-number', label: 'An unknown number' },
        { id: 'number-terms', label: 'The number of terms' },
      ],
      correctUnknown: 'unknown-number',
      modelOptions: [
        {
          id: 'given-equation',
          label: '3x + 6 = 18',
          equation: makeEquation(3, 6, 0, 18),
        },
      ],
      correctModel: ['given-equation'],
      unitOptions: [{ id: 'no-unit', label: 'No unit' }],
      correctUnit: 'no-unit',
      explanationOptions: [
        {
          id: 'divide-all-terms',
          label:
            'Dividing every term on both sides by 3 preserves equality; dividing only 3x does not.',
        },
        {
          id: 'subtract-both-sides',
          label:
            'Subtracting 6 from both sides preserves equality, then each of 3 equal groups is 4.',
        },
        {
          id: 'divide-only-3x',
          label: 'Divide only 3x by 3 and leave the other terms unchanged.',
        },
      ],
      correctChoice: ['divide-all-terms', 'subtract-both-sides'],
      hints: [
        'Try an operation that changes both sides by the same amount.',
        'You can subtract 6 from both sides, or divide every term on both sides by 3.',
        'After isolating x, substitute your value into the original equation.',
      ],
      audioIds: { intro: 'concept-intro' },
    },
    application: {
      id: 'application',
      title: 'Bike rental model',
      prompt:
        'A fictional bike rental costs $6 to start and $3 for each hour. The total is $18. Choose what x means, choose a model, solve it, and answer with units.',
      initialEquation: makeEquation(3, 6, 0, 18),
      expectedSolution: '4',
      unknownChoice: 'What should x stand for?',
      unknownOptions: [
        { id: 'hours-rented', label: 'Hours rented' },
        { id: 'starting-charge', label: 'Starting charge' },
        { id: 'total-cost', label: 'Total cost' },
      ],
      correctUnknown: 'hours-rented',
      modelOptions: [
        {
          id: 'fixed-plus-rate',
          label: '6 + 3x = 18',
          equation: makeEquation(3, 6, 0, 18),
        },
        {
          id: 'rate-plus-fixed',
          label: '3x + 6 = 18',
          equation: makeEquation(3, 6, 0, 18),
        },
        {
          id: 'swapped-rate-fixed',
          label: '6x + 3 = 18',
          equation: makeEquation(6, 3, 0, 18),
        },
      ],
      correctModel: ['fixed-plus-rate', 'rate-plus-fixed'],
      unitOptions: [
        { id: 'hours', label: 'hours' },
        { id: 'dollars', label: 'dollars' },
        { id: 'bikes', label: 'bikes' },
      ],
      correctUnit: 'hours',
      explanationOptions: [
        {
          id: 'rental-substitution',
          label: '$6 + ($3 per hour × 4 hours) = $18.',
        },
        { id: 'bare-four', label: 'The answer is 4.' },
      ],
      correctChoice: ['rental-substitution'],
      hints: [
        'Which charge happens once, and which repeats each hour?',
        'Choose a model before opening the equation workbench.',
        'Check the result by adding the fixed charge to the hourly cost.',
      ],
      audioIds: { intro: 'application-intro' },
    },
    transfer: {
      id: 'transfer',
      title: 'A changed rental',
      prompt:
        'A different fictional rental costs $8 to start and $4 per hour. A receipt totals $20. Build a model, solve it, and interpret the result.',
      initialEquation: makeEquation(4, 8, 0, 20),
      expectedSolution: '3',
      unknownChoice: 'What should x stand for?',
      unknownOptions: [
        { id: 'hours-rented', label: 'Hours rented' },
        { id: 'starting-charge', label: 'Starting charge' },
      ],
      correctUnknown: 'hours-rented',
      modelOptions: [
        {
          id: 'fixed-plus-rate',
          label: '8 + 4x = 20',
          equation: makeEquation(4, 8, 0, 20),
        },
        {
          id: 'rate-plus-fixed',
          label: '4x + 8 = 20',
          equation: makeEquation(4, 8, 0, 20),
        },
        {
          id: 'swapped-rate-fixed',
          label: '8x + 4 = 20',
          equation: makeEquation(8, 4, 0, 20),
        },
      ],
      correctModel: ['fixed-plus-rate', 'rate-plus-fixed'],
      unitOptions: [
        { id: 'hours', label: 'hours' },
        { id: 'dollars', label: 'dollars' },
      ],
      correctUnit: 'hours',
      explanationOptions: [
        {
          id: 'transfer-substitution',
          label: '$8 + ($4 per hour × 3 hours) = $20.',
        },
        { id: 'bare-three', label: 'The answer is 3.' },
      ],
      correctChoice: ['transfer-substitution'],
      hints: [
        'Identify the one-time amount and the amount that repeats.',
        'Apply the same reversible operation to both sides.',
        'Substitute the number of hours into the original model.',
      ],
      audioIds: { intro: 'transfer-intro' },
    },
  },
};
